import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle,
  GitBranch,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";

const CARD_CLASS =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]";

const governancePath = (family, from) =>
  `/discover/governance/${encodeURIComponent(family)}${from ? `?from=${encodeURIComponent(from)}` : ""}`;

function FamilyLink({ family, from }) {
  return (
    <Link to={governancePath(family, from)} className="font-medium text-white hover:underline">
      {family}
    </Link>
  );
}

function EstateSummary({ estate }) {
  if (!estate) {
    return <span className="text-xs text-white/60">Not on the estate</span>;
  }
  const versions = estate.versions || [];
  return (
    <span className="text-xs text-white/80" title={versions.map((v) => `${v.version}: ${v.device_count} devices`).join("\n")}>
      {estate.device_count?.toLocaleString() || 0} devices · {versions.length} version{versions.length === 1 ? "" : "s"}
      {estate.packaging_status ? ` · ${estate.packaging_status}` : ""}
    </span>
  );
}

function ConstraintEditor({ versions, versionMatch, setVersionMatch, version, setVersion }) {
  return (
    <div className="grid gap-2 md:grid-cols-2">
      <div>
        <label className="text-xs text-white/80">Required version</label>
        <select
          value={versionMatch}
          onChange={(e) => setVersionMatch(e.target.value)}
          className="w-full h-9 rounded-md border border-white/20 bg-white/10 px-2 text-sm text-white"
          data-testid="dependency-version-match"
        >
          <option value="any" className="text-black">Any version (product)</option>
          <option value="minimum" className="text-black">Minimum version or newer</option>
          <option value="exact" className="text-black">Specific version only</option>
        </select>
      </div>
      {versionMatch !== "any" && (
        <div>
          <label className="text-xs text-white/80">Version</label>
          <Input
            list="dependency-version-options"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="e.g. 10.0.12"
            data-testid="dependency-version-input"
          />
          <datalist id="dependency-version-options">
            {(versions || []).map((v) => (
              <option key={v.version} value={v.version}>{`${v.device_count} devices`}</option>
            ))}
          </datalist>
        </div>
      )}
    </div>
  );
}

/**
 * App Governance: map which rationalised families this app requires (product
 * or version), which apps require it, and what an upgrade could affect.
 * Relationships also drive dependency packaging and Intune linking.
 */
export default function FamilyDependenciesCard({ familyName, getAccessToken, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [versionMatch, setVersionMatch] = useState("any");
  const [version, setVersion] = useState("");
  const [autoUninstall, setAutoUninstall] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // dependency_family being edited
  const searchSeq = useRef(0);

  const headers = useCallback(async () => {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAccessToken]);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discover/app-dependencies/${encodeURIComponent(familyName)}`, {
        headers: await headers(),
      });
      setData(response.data);
    } catch (error) {
      console.error("Failed to load dependencies:", error);
      toast.error("Failed to load app dependencies");
    } finally {
      setLoading(false);
    }
  }, [familyName, headers]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (!adding || selected || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const params = new URLSearchParams({ search: query.trim(), exclude: familyName, limit: "15" });
        const response = await axios.get(`${API}/discover/dependency-families?${params}`, { headers: await headers() });
        if (seq === searchSeq.current) setResults(response.data.families || []);
      } catch (error) {
        console.error("Family search failed:", error);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, adding, selected, familyName, headers]);

  const resetForm = () => {
    setAdding(false);
    setEditing(null);
    setQuery("");
    setResults([]);
    setSelected(null);
    setVersionMatch("any");
    setVersion("");
    setAutoUninstall(false);
    setNotes("");
  };

  const afterChange = () => {
    resetForm();
    load();
    if (onChange) onChange();
  };

  const saveNew = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/discover/app-dependencies/${encodeURIComponent(familyName)}`,
        { dependency_family: selected.family_name, version_match: versionMatch, version, auto_uninstall: autoUninstall, notes },
        { headers: await headers() }
      );
      toast.success(`${familyName} now requires ${selected.family_name}`);
      afterChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add dependency");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setAdding(false);
    setEditing(row.dependency_family);
    setVersionMatch(row.version_match || "any");
    setVersion(row.version || "");
    setAutoUninstall(!!row.auto_uninstall);
    setNotes(row.notes || "");
  };

  const saveEdit = async (row) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/discover/app-dependencies/${encodeURIComponent(familyName)}`,
        { dependency_family: row.dependency_family, version_match: versionMatch, version, auto_uninstall: autoUninstall, notes },
        { headers: await headers() }
      );
      toast.success("Dependency updated");
      afterChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update dependency");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.dependency_family} as a dependency of ${familyName}?`)) return;
    try {
      const params = new URLSearchParams({ dependency_family: row.dependency_family });
      await axios.delete(`${API}/discover/app-dependencies/${encodeURIComponent(familyName)}?${params}`, {
        headers: await headers(),
      });
      toast.success("Dependency removed");
      afterChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove dependency");
    }
  };

  const requires = data?.requires || [];
  const requiredBy = data?.required_by || [];
  const indirect = (data?.impacted || []).filter((i) => i.depth > 1);
  const impactCount = requiredBy.length + indirect.length;
  const breaking = requiredBy.filter((r) => r.latest_version_meets_constraint === false);
  const pinned = requiredBy.filter((r) => r.version_match === "exact");

  const optionsForm = (versions) => (
    <div className="space-y-3">
      <ConstraintEditor
        versions={versions}
        versionMatch={versionMatch}
        setVersionMatch={setVersionMatch}
        version={version}
        setVersion={setVersion}
      />
      <div className="grid gap-2 md:grid-cols-2">
        <div className="flex items-center justify-between gap-3 rounded border border-white/10 px-3 py-2">
          <div>
            <div className="text-xs text-white">Auto-uninstall previous versions</div>
            <div className="text-[10px] text-white/70">When packaged as a dependency. Off keeps shared runtimes in place.</div>
          </div>
          <Switch checked={autoUninstall} onCheckedChange={setAutoUninstall} />
        </div>
        <div>
          <label className="text-xs text-white/80">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Plugin needs .NET 10" />
        </div>
      </div>
    </div>
  );

  return (
    <Card className={CARD_CLASS} data-testid="family-dependencies-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-white flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Dependencies
          </CardTitle>
          {!adding && !editing && (
            <Button
              size="sm"
              onClick={() => setAdding(true)}
              className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] font-semibold"
              data-testid="add-family-dependency-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add dependency
            </Button>
          )}
        </div>
        <p className="text-xs text-white/80">
          Map the apps {familyName} needs and the apps that need it, so upgrades can be assessed across the estate.
          Dependencies are packaged with auto-uninstall off by default and linked in Intune as auto-install when {familyName} is deployed.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-white/80" /></div>
        ) : (
          <>
            {adding && (
              <div className="p-3 rounded-lg border border-blue-400/40 bg-blue-500/10 space-y-3" data-testid="add-family-dependency-form">
                {!selected ? (
                  <>
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-white/60" />
                      <Input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search your rationalised apps, e.g. .NET Desktop Runtime"
                        className="pl-8"
                        data-testid="family-dependency-search"
                      />
                    </div>
                    {searching && <div className="text-xs text-white/70">Searching…</div>}
                    {!searching && query.trim().length >= 2 && results.length === 0 && (
                      <div className="text-xs text-white/70">No rationalised apps match "{query}".</div>
                    )}
                    {results.length > 0 && (
                      <div className="max-h-72 overflow-y-auto rounded border border-white/10 divide-y divide-white/10">
                        {results.map((fam) => (
                          <button
                            key={fam.family_name}
                            type="button"
                            onClick={() => setSelected(fam)}
                            className="w-full text-left px-3 py-2 hover:bg-white/10"
                            data-testid={`family-result-${fam.family_name}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm text-white">{fam.family_name}</span>
                              <span className="text-xs text-white/70">
                                {fam.source === "packages" ? "Packaged only" : `${fam.device_count.toLocaleString()} devices`}
                              </span>
                            </div>
                            <div className="text-xs text-white/60">
                              {[fam.publisher, fam.versions.slice(0, 4).map((v) => v.version).join(", ")].filter(Boolean).join(" · ")}
                              {fam.versions.length > 4 ? ` +${fam.versions.length - 4} more` : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-white font-medium">{familyName} requires {selected.family_name}</div>
                        <div className="text-xs text-white/60">
                          {selected.winget_id ? `winget: ${selected.winget_id}` : "No winget match - relationship is mapped, but can't be packaged or linked automatically yet"}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => setSelected(null)} title="Choose a different app">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {optionsForm(selected.versions)}
                  </>
                )}
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>
                  <Button size="sm" onClick={saveNew} disabled={!selected || saving} data-testid="save-family-dependency-btn">
                    {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Add dependency
                  </Button>
                </div>
              </div>
            )}

            {/* Upgrade impact */}
            {impactCount > 0 && (
              <div
                className={`flex items-start gap-2 p-3 rounded border text-xs ${
                  breaking.length ? "border-red-500/50 bg-red-500/15 text-red-100" : "border-amber-500/50 bg-amber-500/10 text-amber-100"
                }`}
                data-testid="dependency-impact-banner"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <div>
                  <div className="font-semibold">
                    Upgrading or removing {familyName} affects {impactCount} app{impactCount === 1 ? "" : "s"}
                    {indirect.length ? ` (${indirect.length} indirectly)` : ""}.
                  </div>
                  {breaking.length > 0 && data?.estate?.latest_available_version && (
                    <div className="mt-1">
                      The latest version ({data.estate.latest_available_version}) doesn't meet the requirement of:{" "}
                      {breaking.map((r) => `${r.parent_family} (${r.constraint})`).join(", ")}.
                    </div>
                  )}
                  {pinned.length > 0 && (
                    <div className="mt-1">Pinned to a specific version by: {pinned.map((r) => `${r.parent_family} (${r.version})`).join(", ")}.</div>
                  )}
                </div>
              </div>
            )}

            <section>
              <div className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <ArrowDownRight className="h-4 w-4" /> {familyName} requires ({requires.length})
              </div>
              {requires.length === 0 ? (
                <div className="text-sm text-white/60">No dependencies mapped.</div>
              ) : (
                <div className="space-y-2">
                  {requires.map((row) => (
                    <div key={row.dependency_family} className="p-3 bg-white/10 rounded-lg" data-testid={`requires-row-${row.dependency_family}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <FamilyLink family={row.dependency_family} />
                          <Badge variant="outline" className="text-[10px] text-white border-white/30">{row.constraint}</Badge>
                          <Badge
                            variant="outline"
                            className="text-[10px] text-white/80 border-white/30"
                            title={row.source === "manual" ? `Mapped manually${row.created_by ? ` by ${row.created_by}` : ""}` : "Declared in the winget manifest"}
                          >
                            {row.source === "manual" ? "Manual" : "winget"}
                          </Badge>
                          {row.auto_uninstall && (
                            <Badge variant="outline" className="text-[10px] text-orange-200 border-orange-400/50">auto-uninstall on</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <EstateSummary estate={row.estate} />
                          <Button size="icon" variant="ghost" onClick={() => startEdit(row)} title="Edit" className="text-white/80 hover:text-white">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {row.source === "manual" && (
                            <Button size="icon" variant="ghost" onClick={() => remove(row)} title="Remove" className="text-red-400 hover:text-white hover:bg-red-500">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      {row.devices_not_meeting_constraint > 0 && (
                        <div className="mt-1 text-xs text-amber-200">
                          {row.devices_not_meeting_constraint.toLocaleString()} device{row.devices_not_meeting_constraint === 1 ? "" : "s"} run
                          {" "}{row.dependency_family} versions that don't meet this ({row.versions_not_meeting_constraint.join(", ")}).
                        </div>
                      )}
                      {!row.dependency_winget_id && (
                        <div className="mt-1 text-xs text-white/60">No winget match yet - mapped for impact only, not packaged or linked automatically.</div>
                      )}
                      {row.notes && <div className="mt-1 text-xs text-white/70">{row.notes}</div>}
                      {editing === row.dependency_family && (
                        <div className="mt-3 p-3 rounded border border-blue-400/40 bg-blue-500/10 space-y-3">
                          {optionsForm(row.estate?.versions)}
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>
                            <Button size="sm" onClick={() => saveEdit(row)} disabled={saving}>
                              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <ArrowUpRight className="h-4 w-4" /> Required by ({requiredBy.length}{indirect.length ? ` + ${indirect.length} indirectly` : ""})
              </div>
              {impactCount === 0 ? (
                <div className="text-sm text-white/60">No apps are mapped as requiring {familyName}.</div>
              ) : (
                <div className="space-y-2">
                  {requiredBy.map((row) => (
                    <div key={row.parent_family} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white/10 rounded-lg" data-testid={`required-by-row-${row.parent_family}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <FamilyLink family={row.parent_family} />
                        <span className="text-xs text-white/70">needs {row.constraint}</span>
                        {row.latest_version_meets_constraint === false && (
                          <Badge className="bg-red-600/30 text-red-200 border border-red-500/60 text-[10px]">latest version breaks this</Badge>
                        )}
                        {row.latest_version_meets_constraint === true && row.version_match !== "any" && (
                          <Badge className="bg-green-600/20 text-green-300 border border-green-500/50 text-[10px]">
                            <CheckCircle className="h-3 w-3 mr-0.5" /> latest version OK
                          </Badge>
                        )}
                      </div>
                      <EstateSummary estate={row.estate} />
                    </div>
                  ))}
                  {indirect.map((item) => (
                    <div key={item.family_name} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white/5 rounded-lg">
                      <div className="flex flex-wrap items-center gap-2">
                        <FamilyLink family={item.family_name} />
                        <span className="text-xs text-white/60">indirectly, via {item.via_family}</span>
                      </div>
                      <EstateSummary estate={item.estate} />
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}
